// Имоти на наемодателя/пропърти мениджъра: търсене на живо (адрес, град, име или
// телефон на наемателя) и подредба. По подразбиране просрочените са отгоре —
// подредбата идва готова от сървъра (смята се в базата).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, money } from '../../api';
import { useAuth } from '../../AuthContext';
import { useWide } from '../../layout';
import { confirmAction, showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Input, Muted, Screen, Title } from '../../ui';
import { cityLabel } from '../../geo';
import { getLocale, t } from '../../i18n';

// Функция, а не константа — етикетите се преизчисляват при смяна на езика
const SORTS = (): { key: string; label: string }[] => [
  { key: 'debt', label: t('Неплатени') },
  { key: 'address', label: t('Адрес') },
  { key: 'rent', label: t('Наем') },
  { key: 'created', label: t('Най-нови') },
];

export default function ApartmentsScreen({ navigation }: any) {
  const { user } = useAuth();
  const wide = useWide();
  const [apartments, setApartments] = useState<any[]>([]);
  const [restricted, setRestricted] = useState(false);
  const [q, setQ] = useState('');
  // отвореното меню „⋯“ (id на имота) — по едно наведнъж
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // подредбата по подразбиране е „неплатени отгоре“ при ВСЯКО влизане
  const [sort, setSort] = useState('debt');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((query: string, sortKey: string) => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    params.set('sort', sortKey);
    api(`/apartments?${params}`)
      .then((r) => setApartments(r.apartments))
      .catch(() => {});
  }, []);

  useFocusEffect(
    useCallback(() => {
      setSort('debt'); // всяко влизане започва с неплатените отгоре
      load(q, 'debt');
      api('/billing/subscription').then((r) => setRestricted(!r.access?.active)).catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load])
  );

  // търсене „на живо“ с малко изчакване, за да не бомбардира сървъра
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(q, sort), 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [q, sort, load]);

  return (
    <Screen>
      <Title>{t('Моите имоти')}</Title>
      {restricted && (
        <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
          <Badge text={t('ОГРАНИЧЕН РЕЖИМ')} color={COLORS.danger} />
          <Muted>
            {t('Абонаментът е изтекъл — само преглед. Обявите са свалени, наемателите ви не могат да плащат през приложението.')}
          </Muted>
          <Button title={t('Поднови абонамента →')} onPress={() => navigation.navigate('Subscription')} />
        </Card>
      )}
      <Button title={t('+ Нов имот')} onPress={() => navigation.navigate('AddApartment')} />

      <Input
        value={q}
        onChangeText={setQ}
        placeholder={t('Търси по адрес, наемател или телефон…')}
        autoCapitalize="none"
        style={{ marginTop: 12 }}
      />

      <View style={{ flexDirection: 'row', gap: 6, marginTop: 8 }}>
        {SORTS().map((s) => (
          <Pressable
            key={s.key}
            onPress={() => setSort(s.key)}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 8,
              alignItems: 'center',
              backgroundColor: sort === s.key ? COLORS.primary : COLORS.card,
              borderWidth: 1,
              borderColor: sort === s.key ? COLORS.primary : COLORS.border,
            }}
          >
            <Text style={{ color: sort === s.key ? '#fff' : COLORS.text, fontSize: 13 }}>{s.label}</Text>
          </Pressable>
        ))}
      </View>

      <View style={{ height: 12 }} />
      {/* На широк екран картите са в две колони — една дълга колона чете
          се като телефон, не като система */}
      <View style={wide ? { flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' } : undefined}>
      {apartments.length === 0 && (
        <Card>
          <Muted>{q ? t('Няма намерен имот по това търсене.') : t('Още нямате добавени имоти.')}</Muted>
        </Card>
      )}
      {apartments.map((a) => {
        const tenancy = a.tenancies[0];
        const overdue = a.overdueCents > 0;
        return (
          <Pressable
            key={a.id}
            onPress={() => navigation.navigate('ApartmentDetail', { id: a.id })}
            style={wide ? { width: '48.9%' } : undefined}
          >
            <Card style={overdue ? { borderColor: COLORS.danger, borderWidth: 2 } : {}}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {overdue && <Text style={{ color: COLORS.danger, fontSize: 18, marginRight: 6 }}>●</Text>}
                <Text style={{ fontSize: 17, fontWeight: '600', color: COLORS.text, flex: 1 }}>{a.address}</Text>
                {/* „⋯“ отваря действията, без да влиза в имота */}
                <Pressable
                  onPress={() => setMenuFor(menuFor === a.id ? null : a.id)}
                  hitSlop={10}
                  style={{ paddingHorizontal: 8, paddingVertical: 2 }}
                >
                  <Text style={{ fontSize: 20, color: COLORS.muted, fontWeight: '700' }}>⋯</Text>
                </Pressable>
              </View>

              {menuFor === a.id && (
                <View style={{ marginTop: 8, gap: 6 }}>
                  {user?.role === 'AGENCY' && (
                    <Button
                      title={t('👤 Собственик — добави / смени')}
                      variant="secondary"
                      onPress={() => {
                        setMenuFor(null);
                        navigation.navigate('AssignOwner', { apartmentId: a.id });
                      }}
                    />
                  )}
                  {/* Обитатели: живеещи в имота, които НЕ са страна по наема (рядък случай) */}
                  <Button
                    title={t('👥 Обитатели')}
                    variant="secondary"
                    onPress={() => {
                      setMenuFor(null);
                      navigation.navigate('Residents', { apartmentId: a.id, address: a.address });
                    }}
                  />
                  <Button
                    title={t('🗄️ Архивирай имота')}
                    variant="secondary"
                    onPress={() => {
                      setMenuFor(null);
                      if (tenancy) {
                        showError(t('Имот с наемател не може да се архивира — първо приключете наемното отношение.'));
                        return;
                      }
                      confirmAction(
                        t('Архивиране'),
                        t('Наистина ли архивирате {address}? Историята се запазва; връщане — от Още → „Архивирани имоти“.', { address: a.address }),
                        t('Архивирай'),
                        async () => {
                          try {
                            await api(`/apartments/${a.id}`, { method: 'DELETE' });
                            showSuccess(t('Имотът е в архива.'));
                            load(q, sort);
                          } catch (e: any) {
                            showError(e.message);
                          }
                        }
                      );
                    }}
                  />
                </View>
              )}
              <Muted>{cityLabel(a.city, a.region)}{a.rooms ? ` · ${t('{n} стаи', { n: a.rooms })}` : ''}{a.floor != null ? ` · ${t('ет. {n}', { n: a.floor })}` : ''}</Muted>

              {overdue && (
                <View style={{ marginTop: 6 }}>
                  <Badge text={t('Просрочено: {amount}', { amount: money(a.overdueCents) })} color={COLORS.danger} />
                  {a.oldestOverdue && (
                    <Muted>{t('Най-старо просрочие от {date}', { date: new Date(a.oldestOverdue).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}</Muted>
                  )}
                </View>
              )}
              {!overdue && a.awaitingCount > 0 && (
                <View style={{ marginTop: 6 }}>
                  <Badge text={t('Чака потвърждение: {n}', { n: a.awaitingCount })} color="#D97706" />
                </View>
              )}

              <View style={{ marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                {tenancy ? (
                  <>
                    <Badge
                      text={tenancy.status === 'ACTIVE' ? t('Отдаден') : t('Чака регистрация')}
                      color={tenancy.status === 'ACTIVE' ? COLORS.success : '#D97706'}
                    />
                    <Text style={{ fontWeight: '600', color: COLORS.text }}>{t('{amount}/мес.', { amount: money(tenancy.monthlyRentCents) })}</Text>
                  </>
                ) : (
                  <Badge text={t('Свободен')} color={COLORS.muted} />
                )}
              </View>
            </Card>
          </Pressable>
        );
      })}
      </View>
    </Screen>
  );
}
